import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutGrid, SquareSlash } from 'lucide-react';
import ToggleButton from '../common/button/ToggleButton';
import ServiceLogo from '../common/logo/ServiceLogo';
import TooltipPortal from '../common/tooltip/TooltipPortal';
import { fetchSettings, updateSettings } from '../../api/settings';
import { fetchServices, toggleService } from '../../api/services';

import styles from './styles/ServicesSelector.module.css';

const ServicesDropdown = ({
    position = 'top',
    services,
    onToggleService,
    enableServices,
    onToggleEnableServices,
}) => {
    const dropdownRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(null);
    const [hoveredWarning, setHoveredWarning] = useState(null);

    const sortedServices = useMemo(() => {
        return [...services].sort((a, b) => {
            if (a.isDefault === b.isDefault) return 0;
            return a.isDefault ? -1 : 1;
        });
    }, [services]);

    const updateDropdownHeight = useCallback(() => {
        const margin = 25;
        if (dropdownRef.current) {
            if (position === 'top') {
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const spaceAbove = dropdownRect.bottom - margin;
                setMaxHeight(spaceAbove);
            } else {
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - dropdownRect.top - margin;
                setMaxHeight(spaceBelow);
            }
        }
    }, [position]);

    useEffect(() => {
        updateDropdownHeight();

        const handleResize = () => {
            updateDropdownHeight();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateDropdownHeight]);

    return (
        <motion.div
            ref={dropdownRef}
            className={`${styles.dropdown} ${styles[position]}`}
            style={{
                maxHeight: maxHeight ? `${maxHeight}px` : 'none',
                animation: 'none',
                transformOrigin: position === 'top' ? 'bottom left' : 'top left',
            }}
            initial={{ opacity: 0, y: position === 'top' ? -8 : 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: position === 'top' ? -6 : 6, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 0.2 }}
        >
            <div className={styles.setting}>
                <span>Enable Services</span>
                <ToggleButton
                    isEnabled={enableServices}
                    onChange={onToggleEnableServices}
                    size="small"
                />
            </div>
            <div>
                {sortedServices?.length > 0 && sortedServices.map(service => (
                    <div key={service.id} className={styles.serviceItemContainer}>
                        <div
                            className={`${styles.serviceItem} ${!enableServices ? styles.disabled : ''}`}
                            onClick={() => {
                                if (enableServices) {
                                    onToggleService(service.id);
                                }
                            }}
                        >
                            <div className={styles.serviceInfo}>
                                <ServiceLogo
                                    logo={service.logo}
                                    name={service.name}
                                    size="tiny"
                                />
                                <span className={styles.serviceName}>{service.name}</span>
                                {(service.requiresAuth && !service.hasUserToken) || (service.missingUserSecretsCount > 0) ? (
                                    <TooltipPortal
                                        tooltip={`${(service.requiresAuth && !service.hasUserToken ? 'OAuth required' : '')}
                                            ${((service.requiresAuth && !service.hasUserToken) && (service.missingUserSecretsCount > 0) ? ' • ' : '')}
                                            ${service.missingUserSecretsCount > 0 ? 'Missing secrets' : ''}
                                            ${` - Click to open settings`}`}
                                        show={hoveredWarning === service.id}
                                    >
                                        <div
                                            className={styles.warningDotWrapper}
                                            onMouseDown={e => { e.stopPropagation(); }}
                                            onMouseEnter={() => setHoveredWarning(service.id)}
                                            onMouseLeave={() => setHoveredWarning(null)}
                                        >
                                            <button
                                                className={styles.warningDotButton}
                                                aria-label="Open service settings"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    window.open(`/store/added?serviceId=${service.id}`, '_blank');
                                                }}
                                            />
                                        </div>
                                    </TooltipPortal>
                                ) : null}
                            </div>
                            <ToggleButton
                                isEnabled={service.isEnabled}
                                onChange={() => {}}
                                size="small"
                                disabled={!enableServices}
                            />
                        </div>

                        {!enableServices && (
                            <span className={styles.tooltip}>
                                Services are disabled
                            </span>
                        )}
                    </div>
                ))}

                {services.length === 0 && (
                    <div className={styles.emptyState}>
                        <span>No services added yet</span>
                        <a href="/store/discover" className={styles.storeLink}>
                            Go to Store
                        </a>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

const ServicesSelector = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [services, setServices] = useState([]);
    const [enableServices, setEnableServices] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const containerRef = useRef(null);

    const loadServices = useCallback(async () => {
        try {
            const data = await fetchServices('added');
            setServices(data.services);
        } catch (error) {
            console.error('Error loading services:', error);
        }
    }, []);

    const loadSettings = useCallback(async () => {
        try {
            const data = await fetchSettings('services');
            setEnableServices(data.services?.enableServices ?? true);
        } catch (error) {
            console.error('Error loading settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
        loadServices();
    }, [loadServices, loadSettings]);

    useEffect(() => {
        const handleClickOutside = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleService = useCallback(async serviceId => {
        try {
            await toggleService(serviceId);
            setServices(prev => prev.map(service => 
                service.id === serviceId 
                    ? { ...service, isEnabled: !service.isEnabled }
                    : service
            ));
        } catch (error) {
            console.error('Error toggling service:', error);
        }
    }, []);

    const handleToggleEnableServices = useCallback(async () => {
        try {
            const newValue = !enableServices;
            await updateSettings({
                services: {
                    enableServices: newValue,
                }
            });
            setEnableServices(newValue);
            await loadServices();
        } catch (error) {
            console.error('Error updating settings:', error);
        }
    }, [enableServices, loadServices]);

    if (isLoading) return null;

    return (
        <div className={styles.container} ref={containerRef}>
            <button
                className={`${styles.selector} ${isOpen ? styles.selected : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle services"
            >
                {enableServices && services?.length > 0 ? (
                    <LayoutGrid size={18} />
                ) : (
                    <SquareSlash size={18} />
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <ServicesDropdown
                        services={services}
                        onToggleService={handleToggleService}
                        enableServices={enableServices}
                        onToggleEnableServices={handleToggleEnableServices}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default memo(ServicesSelector);
